import React from 'react';
import {
  Paper,
  Box,
  Typography,
  SvgIcon,
  Stack,
  PaperProps,
  Tooltip,
} from '@mui/material';
import { ReactComponent as mapLocation } from 'assets/mapLocation.svg';
import { colors } from 'styles/colors';
import TypographyWithI18N from 'shared/components/TypographyWithI18N';
import { completeDateFormat } from 'utilities/formatDate';
import {
  IStageMetaData,
  RequisitionStage,
} from 'modules/hiring/models/myOpenRequisition.model';
import RequisitionChart from './requisitionChart';
import { useTranslation } from 'react-i18next';
import { getStageColor } from 'modules/hiring/utilities';
import { areStringsEqual } from 'utilities/utilityFunctions';

export interface IRequisitionTileProps extends PaperProps {
  createdDate: string;
  curentStep: string;
  currentStage: RequisitionStage;
  id: string;
  lastUpdated: string | null;
  owner: {
    firstName: string;
    middleName: string | null;
    lastName: string;
    role: string;
  };
  reqId: string;
  state: string;
  city: string;
  country: string;
  reqName: string;
  remoteLocation: boolean;
  stagesMeta: IStageMetaData[];
  onHiringTileClick: CallableFunction;
}

const RequisitionTile: React.FC<IRequisitionTileProps> = ({
  createdDate,
  curentStep,
  currentStage,
  id,
  lastUpdated,
  owner,
  reqId,
  reqName,
  state,
  city,
  country,
  stagesMeta,
  remoteLocation,
  onHiringTileClick,
  ...props
}) => {
  const { t } = useTranslation();
  const isHiringStage = areStringsEqual(currentStage, RequisitionStage.Hiring);

  const location = [city, state, country].filter(Boolean).join(', ');

  return (
    <Paper
      id={id}
      data-testid={id}
      {...props}
      onClick={(e) => {
        if (isHiringStage) onHiringTileClick();
        props.onClick?.(e);
      }}
      onKeyDown={(e) => {
        if (isHiringStage && e.key === 'Enter') onHiringTileClick();
        props.onKeyDown?.(e);
      }}
      tabIndex={isHiringStage ? 0 : undefined}
      aria-label={`Open Requsition ${reqName} ${reqId}`}
      role={isHiringStage ? 'link' : undefined}
      sx={{
       width: '100%',
        maxWidth: '100%',
        p: ({ typography }) => typography.pxToRem(20),
        borderColor: `${colors.background.darkGray}`,
        borderRadius: ({ typography }) => typography.pxToRem(10),
        boxSizing: 'border-box',
        boxShadow: 'none',
        cursor: isHiringStage ? 'pointer' : '',
        ':focus-visible': {
          outline: isHiringStage ? `2px solid ${colors.black}` : '',
          margin: ({ typography }) => typography.pxToRem(2),
        },
        backgroundColor: isHiringStage
          ? 'initial'
          : colors.background.lightGray,
        '&:hover': {
          backgroundColor: isHiringStage
            ? colors.state.hover
            : colors.background.lightGray,
        },
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: '100%',
        }}
      >
        <Tooltip title={`${reqName}`}>
          <Typography
            className='kfic2-smallHeader'
            id={id + '-reqName'}
            data-testid={id + '-reqName'}
            component='h3'
            sx={{
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
            }}
          >
            {reqName}
          </Typography>
        </Tooltip>
      </Box>
      <Stack
        direction='row'
        id={`${id}-reqdId-container`}
        data-testid={`${id}-reqdId-container`}
        mt={({ typography }) => typography.pxToRem(8)}
      >
        <TypographyWithI18N
          className='kfic2-bodyText'
          id={`${id}-reqId-label`}
          data-testid={`${id}-reqId-label`}
          i18nKey='reqId'
          textSuffix=':'
        />
        <Typography
          className='kfic2-bodyTextBold'
          id={`${id}-reqId`}
          data-testid={`${id}-reqId`}
          ml={({ typography }) => typography.pxToRem(8)}
        >
          {reqId}
        </Typography>
      </Stack>
      <Stack
        direction='row'
        id={`${id}-location-container`}
        data-testid={`${id}-location-container`}
        mt={0.5}
      >
        <SvgIcon
          component={mapLocation}
          id={`${id}-locationPin`}
          data-testid={`${id}-locationPin`}
        />
        <Tooltip title={location || ''} arrow placement='top'>
          <div
            style={{
              width: '100%',
              maxWidth: '377px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            <Typography
              className='kfic2-bodyText'
              id={`${id}-location`}
              data-testid={`${id}-location`}
              ml={({ typography }) => typography.pxToRem(8)}
              style={{
                width: '100%',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                cursor: 'pointer',
              }}
            >
              {location}
              {remoteLocation ? ` / ${t('remote')}` : ''}
            </Typography>
          </div>
        </Tooltip>
      </Stack>
      <Box
        sx={{
          backgroundColor: `${getStageColor(currentStage, stagesMeta)}26`,
          borderRadius: ({ typography }) => typography.pxToRem(8),
          p: ({ typography }) => typography.pxToRem(8),
          mt: ({ typography }) => typography.pxToRem(16),
          px: ({ typography }) => typography.pxToRem(16),
          py: ({ typography }) => typography.pxToRem(12),
        }}
      >
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'
          id={`${id}-stage-date-container`}
          data-testid={`${id}-stage-date-container`}
        >
          <Typography
            className='kfic2-smallHeaderBold'
            id={`${id}-currentStage`}
            data-testid={`${id}-currentStage`}
          >
            {currentStage}
          </Typography>
          <Stack
            direction='row'
            id={`${id}-date-container`}
            data-testid={`${id}-date-container`}
          >
            <TypographyWithI18N
              className='kfic2-metadata'
              id={`${id}-date-label`}
              data-testid={`${id}-date-label`}
              i18nKey={lastUpdated ? 'updated' : 'created'}
              textSuffix=':'
              mr={(theme: { typography: { pxToRem: (value: number) => string } }) => theme.typography.pxToRem(8)}
            />
            <Typography
              className='kfic2-bodyTextSmall'
              id={`${id}-date`}
              data-testid={`${id}-date`}
              fontWeight='bold'
            >
              {completeDateFormat(lastUpdated || createdDate)}
            </Typography>
          </Stack>
        </Stack>
        <RequisitionChart
          id={id + '-chart'}
          currentStep={curentStep}
          stages={stagesMeta}
          currentStage={currentStage}
          mt={({ typography }) => typography.pxToRem(16)}
        />
        <Stack
          direction='row'
          id={`${id}-currentStep-container`}
          data-testid={`${id}-currentStep-container`}
          mt={({ typography }) => typography.pxToRem(8)}
        >
          <TypographyWithI18N
            className='kfic2-bodyTextBold'
            i18nKey={isHiringStage ? 'mostRecentStep' : 'currentStep'}
            textSuffix=':'
            id={`${id}-currentStep-label`}
            data-testid={`${id}-currentStep-label`}
          />
          <Typography
            className='kfic2-bodyText'
            id={`${id}-currentStep`}
            data-testid={`${id}-currentStep`}
            ml={({ typography }) => typography.pxToRem(12)}
          >
            {curentStep}
          </Typography>
        </Stack>
      </Box>
    </Paper>
  );
};

export default RequisitionTile;
